import { useCallback, useContext, useEffect, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NonIdealNew from "../../../../common/NonIdealNew";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import { FilterNonIdeal } from "../../../../common";
import styles from "../styles.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import { formatReportCurrency, getStartAndEndDate, REPORT_DATE_FILTERS } from "../../../../../utils";
import dayjs from "dayjs";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const SUMMARY_SKELETONS = [
  { labelWidth: 150, valueWidth: 88  },
  { labelWidth: 110, valueWidth: 100 },
  { labelWidth: 105, valueWidth: 80  },
];
const TAX_RATES_SKELETON_ROWS = 3;

const TaxSummary = () => {
  const [I18n] = useContext(LanguageContext);
  const { handleFetchReport, loading, taxSummary } = useReports(I18n);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchReport = useCallback((start, end) => {
    handleFetchReport({
      business_id: business?.id,
      report_type: "tax_summary",
      start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
      end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
    });
  }, [handleFetchReport, business?.id]);


  useEffect(() => {
    triggerLogEvent(analytics, "report_tax_summary_visited", {});
  }, []);

  const { handleExport, exportLoading } = useReportExport("tax_summary");
  const { headerDefaultFilter, isYearlyFilter, resetCount, handleFilterDate, handleResetFilter } = useReportFilter(fetchReport, "tax_summary");

  const summaryRows = useMemo(() => [
    { labelKey: "total_tax_collected", value: formatReportCurrency(taxSummary.total_tax_collected) },
    { labelKey: "taxable_sales",       value: formatReportCurrency(taxSummary.taxable_sales) },
    { labelKey: "tax_free_sales",      value: formatReportCurrency(taxSummary.tax_free_sales) },
  ], [taxSummary]);

  const hasNoData = !taxSummary.total_tax_collected &&
    !taxSummary.taxable_sales &&
    !taxSummary.tax_free_sales &&
    taxSummary.multiple_tax_rates.length === 0;

  const showNonIdeal = isYearlyFilter && !loading && hasNoData;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="tax_summary_page_wrapper" className={styles.pageWrapper}>
      <ReportPageHeader
        title={I18n.tax_summary}
        id="tax_summary"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />

      {showNonIdeal ? (
        <Box id="tax_summary_nonideal_body" className={styles.pageBody}>
          <NonIdealNew
            buttonOneId="tax_summary_nonideal_start_sale_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.start_new_sale}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_sale", { report_type: "tax_summary" });
              navigate("/QuickSale/QuickSaleForm", { state: { pathname: location.pathname } });
            }}
            loading={false}
            disabledOne={false}
          />
        </Box>
      ) : (
        <Box id="tax_summary_page_body" className={styles.pageBody} style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {!loading && hasNoData ? (
            <FilterNonIdeal
              title={I18n.no_transactions_matching_those_filters}
              subTitle={`${I18n.try_adjusting_filters} `}
              subTitleText={I18n.view_yearly_data}
              subTitleTextOnClick={handleResetFilter}
              subTitleTextId="tax_summary_filter_view_yearly"
            />
          ) : (
            <>
              {/* Fixed 3-row summary section */}
              <Box id="tax_summary_summary_section" style={{ flexShrink: 0 }}>
                {loading
                  ? SUMMARY_SKELETONS.map(({ labelWidth, valueWidth }, i) => (
                      <Box key={i} className={styles.summaryRow}>
                        <Skeleton variant="rounded" animation="wave" width={labelWidth} height={18} sx={{ borderRadius: "6px" }} />
                        <Skeleton variant="rounded" animation="wave" width={valueWidth} height={18} sx={{ borderRadius: "6px" }} />
                      </Box>
                    ))
                  : summaryRows.map(({ labelKey, value }) => (
                      <Box key={labelKey} id={`tax_summary_row_${labelKey}`} className={styles.summaryRow}>
                        <ParagraphText id={`tax_summary_label_${labelKey}`} className={styles.summaryLabel}>{I18n[labelKey]}</ParagraphText>
                        <ParagraphText id={`tax_summary_value_${labelKey}`} className={styles.summaryValue}>{value}</ParagraphText>
                      </Box>
                    ))}
              </Box>

              {/* Multiple tax rates — header fixed, rows scrollable */}
              <Box id="tax_summary_rates_section" style={{ marginTop: 24, display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                {(loading || taxSummary.multiple_tax_rates.length > 0) && (
                  <Box id="tax_summary_rates_header_wrapper" style={{ flexShrink: 0 }}>
                    <ReportPageHeader title={I18n.multiple_tax_rates} id="multiple_tax_rates" hideActions />
                    <Box id="tax_summary_rates_table_header" className="invoiceListTableHeader">
                      <Box id="tax_summary_header_tax_rate" className="invListTableHeaderCol threeColumn textLeft">
                        <ParagraphText id="tax_summary_header_label_tax_rate" className="fontSize12 fontWeight500 newLightColor">{I18n.tax_rate}</ParagraphText>
                      </Box>
                      <Box id="tax_summary_header_orders" className="invListTableHeaderCol threeColumn textLeft">
                        <ParagraphText id="tax_summary_header_label_orders" className="fontSize12 fontWeight500 newLightColor">{I18n.orders}</ParagraphText>
                      </Box>
                      <Box id="tax_summary_header_tax_collected" className="invListTableHeaderCol threeColumn textRight">
                        <ParagraphText id="tax_summary_header_label_tax_collected" className="fontSize12 fontWeight500 newLightColor">{I18n.tax_collected}</ParagraphText>
                      </Box>
                    </Box>
                  </Box>
                )}

                <Box id="tax_summary_rates_scroll" style={{ overflowY: "auto", flex: 1 }} className="generic-scrollbar">
                  {loading
                    ? Array.from({ length: TAX_RATES_SKELETON_ROWS }).map((_, i) => (
                        <Box key={i} className="invoiceListTableBody defaultCursorImportant">
                          <Box className="invListTableHeaderCol threeColumn textLeft">
                            <Skeleton variant="rounded" animation="wave" width={40} height={16} sx={{ borderRadius: "6px" }} />
                          </Box>
                          <Box className="invListTableHeaderCol threeColumn textLeft">
                            <Skeleton variant="rounded" animation="wave" width={36} height={16} sx={{ borderRadius: "6px" }} />
                          </Box>
                          <Box className="invListTableHeaderCol threeColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                          </Box>
                        </Box>
                      ))
                    : taxSummary.multiple_tax_rates.map(({ tax_rate, orders, tax_collected }, i) => (
                        <Box key={i} id={`tax_summary_rate_row_${i}`} className="invoiceListTableBody defaultCursorImportant">
                          <Box id={`tax_summary_rate_row_${i}_tax_rate`} className="invListTableHeaderCol threeColumn textLeft">
                            <ParagraphText id={`tax_summary_cell_${i}_tax_rate`} className="fontSize14 newFontColor">
                              {tax_rate != null ? `${tax_rate}%` : "—"}
                            </ParagraphText>
                          </Box>
                          <Box id={`tax_summary_rate_row_${i}_orders`} className="invListTableHeaderCol threeColumn textLeft">
                            <ParagraphText id={`tax_summary_cell_${i}_orders`} className="fontSize14 newFontColor">{orders}</ParagraphText>
                          </Box>
                          <Box id={`tax_summary_rate_row_${i}_tax_collected`} className="invListTableHeaderCol threeColumn textRight">
                            <ParagraphText id={`tax_summary_cell_${i}_tax_collected`} className="fontSize14 newFontColor">{formatReportCurrency(tax_collected)}</ParagraphText>
                          </Box>
                        </Box>
                      ))}
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TaxSummary;
